import { useEffect, useState } from "react";
import { ReviewCard } from "./ReviewCard";
import { StarRating } from "./StarRating";
import type { PublicReview } from "@/types";

type Page = { items: PublicReview[]; nextCursor: string | null; avgRating: number | null; reviewCount: number };

export function ReviewList({ therapistId }: { therapistId: string }) {
  const [items, setItems] = useState<PublicReview[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ avg: number | null; count: number }>({ avg: null, count: 0 });
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  async function load(c: string | null) {
    setLoading(true);
    const qs = new URLSearchParams({ limit: "10" });
    if (c) qs.set("cursor", c);
    const res = await fetch(`/api/therapists/${therapistId}/reviews?${qs.toString()}`);
    const page = (await res.json()) as Page;
    setItems((prev) => (c ? [...prev, ...page.items] : page.items));
    setCursor(page.nextCursor);
    setSummary({ avg: page.avgRating, count: page.reviewCount });
    setHasLoaded(true);
    setLoading(false);
  }

  useEffect(() => { load(null); }, [therapistId]);

  if (hasLoaded && summary.count === 0) {
    return (
      <section className="space-y-4">
        <p className="text-sm text-neutral-500">Nema recenzija</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Recenzije</h2>
        {summary.count > 0 ? (
          <span className="flex items-center gap-2 text-sm text-neutral-600">
            <StarRating value={Math.round(summary.avg ?? 0)} readOnly size="sm" />
            {summary.avg?.toFixed(1)} ({summary.count})
          </span>
        ) : null}
      </header>
      <ul className="space-y-3">
        {items.map((r) => <li key={r.id}><ReviewCard review={r} /></li>)}
      </ul>
      {cursor ? (
        <button type="button" onClick={() => load(cursor)} disabled={loading} className="rounded-md border px-3 py-1 text-sm">
          {loading ? "Učitavanje…" : "Učitaj još"}
        </button>
      ) : null}
    </section>
  );
}
